import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Star, 
  MapPin, 
  Calendar, 
  Clock, 
  Car, 
  Phone,
  MessageCircle,
  Heart,
  Share2,
  Navigation,
  Search,
  Wallet,
  CheckCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import paymentService from '../services/paymentService';

const Connects = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [connects, setConnects] = useState([]);
  const [filteredConnects, setFilteredConnects] = useState([]);
  const [connectedConnects, setConnectedConnects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [genderSpecificity, setGenderSpecificity] = useState(false);
  const [preferredGender, setPreferredGender] = useState('any');
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedConnect, setSelectedConnect] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('wallet');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [walletBalance, setWalletBalance] = useState(paymentService.getWalletBalance());

  // Dummy connects data
  const dummyConnects = [
    {
      id: 1,
      user: {
        name: 'Priya Sharma',
        rating: 4.8,
        rides: 45,
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
        phone: '+91-98765-43210',
        verified: true,
        gender: 'female'
      },
      ride: {
        from: 'Mumbai Central',
        to: 'Andheri West',
        date: '2024-01-15',
        time: '09:00 AM',
        price: 120,
        vehicle: 'Honda City',
        seats: 2,
        distance: '12 km',
        duration: '45 min'
      },
      status: 'active',
      description: 'Regular office commute. Prefer non-smokers. Will share fuel cost.',
      preferences: ['Non-smokers only', 'Music lovers welcome'],
      connectType: 'premium',
      connectPrice: 50,
      originalPrice: 80
    },
    {
      id: 2,
      user: {
        name: 'Rahul Mehta',
        rating: 4.6,
        rides: 32,
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
        phone: '+91-98765-43211',
        verified: true,
        gender: 'male'
      },
      ride: {
        from: 'Bandra West',
        to: 'Powai',
        date: '2024-01-15',
        time: '08:30 AM',
        price: 150,
        vehicle: 'Maruti Swift',
        seats: 1,
        distance: '18 km',
        duration: '55 min'
      },
      status: 'active',
      description: 'Early morning ride. Music lovers welcome!',
      preferences: ['Early bird', 'Professional environment'],
      connectType: 'standard',
      connectPrice: 30,
      originalPrice: 40
    },
    {
      id: 3,
      user: {
        name: 'Anjali Patel',
        rating: 4.9,
        rides: 67,
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
        phone: '+91-98765-43212',
        verified: true,
        gender: 'female'
      },
      ride: {
        from: 'Thane',
        to: 'BKC',
        date: '2024-01-16',
        time: '07:45 AM',
        price: 200,
        vehicle: 'Hyundai i20',
        seats: 3,
        distance: '25 km',
        duration: '75 min'
      },
      status: 'active',
      description: 'Corporate ride. Professional environment preferred.',
      preferences: ['Professional environment', 'Strict timing'],
      connectType: 'premium',
      connectPrice: 75,
      originalPrice: 100
    },
    {
      id: 4,
      user: {
        name: 'Vikram Singh',
        rating: 4.7,
        rides: 28,
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
        phone: '+91-98765-43213',
        verified: false,
        gender: 'male'
      },
      ride: {
        from: 'Navi Mumbai',
        to: 'Fort',
        date: '2024-01-15',
        time: '10:00 AM',
        price: 180,
        vehicle: 'Tata Nexon',
        seats: 2,
        distance: '30 km',
        duration: '90 min'
      },
      status: 'active',
      description: 'Weekend shopping trip. Flexible timing.',
      preferences: ['Flexible timing', 'Luggage space available'],
      connectType: 'standard',
      connectPrice: 25,
      originalPrice: 35
    },
    {
      id: 5,
      user: {
        name: 'Sneha Reddy',
        rating: 4.5,
        rides: 19,
        avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face',
        phone: '+91-98765-43214',
        verified: false,
        gender: 'female'
      },
      ride: {
        from: 'Kurla',
        to: 'Worli',
        date: '2024-01-16',
        time: '08:15 AM',
        price: 100,
        vehicle: 'Maruti Alto',
        seats: 1,
        distance: '15 km',
        duration: '50 min'
      },
      status: 'active',
      description: 'Student-friendly ride. Affordable pricing.',
      preferences: ['Student-friendly', 'Affordable pricing'],
      connectType: 'budget',
      connectPrice: 15,
      originalPrice: 25
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setConnects(dummyConnects);
      setFilteredConnects(dummyConnects);
      setLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    let filtered = connects;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(connect =>
        connect.user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        connect.ride.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
        connect.ride.to.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply category filter
    if (selectedFilter !== 'all') {
      filtered = filtered.filter(connect => connect.connectType === selectedFilter);
    }

    // Apply gender specificity filter
    if (genderSpecificity && preferredGender !== 'any') {
      filtered = filtered.filter(connect => connect.user.gender === preferredGender);
    }

    setFilteredConnects(filtered);
  }, [searchTerm, selectedFilter, connects, genderSpecificity, preferredGender]);

  const handleFavorite = (connectId) => {
    setFavorites(prev => 
      prev.includes(connectId) 
        ? prev.filter(id => id !== connectId)
        : [...prev, connectId]
    );
  };

  const handleConnect = (connect) => {
    setSelectedConnect(connect);
    setShowPaymentModal(true);
    setPaymentSuccess(false);
  };

  const handlePayment = async () => {
    setPaymentLoading(true);
    
    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Process payment
      const success = paymentService.processPayment(selectedConnect.connectPrice, paymentMethod);
      
      if (success) {
        setPaymentSuccess(true);
        setWalletBalance(paymentService.getWalletBalance());
        
        // Add to connected connects
        const connectedConnect = {
          ...selectedConnect,
          status: 'connected',
          connectedAt: new Date().toISOString()
        };
        setConnectedConnects(prev => [...prev, connectedConnect]);
        
        // Save to localStorage for Dashboard
        const existingConnectedRides = JSON.parse(localStorage.getItem('connectedRides') || '[]');
        const updatedConnectedRides = [...existingConnectedRides, connectedConnect];
        localStorage.setItem('connectedRides', JSON.stringify(updatedConnectedRides));
        
        // Remove from available connects
        setConnects(prev => prev.filter(c => c.id !== selectedConnect.id));
        setFilteredConnects(prev => prev.filter(c => c.id !== selectedConnect.id));
        
        // Auto-redirect to chat after 2 seconds
        setTimeout(() => {
          setShowPaymentModal(false);
          setPaymentSuccess(false);
          setSelectedConnect(null);
          navigate(`/chat/${selectedConnect.id}`);
        }, 2000);
      } else {
        alert('Payment failed. Please try again.');
      }
    } catch (error) {
      alert('Payment failed. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleCall = (phone) => {
    alert(`Calling ${phone}`);
  };

  const handleMessage = (userName) => {
    alert(`Opening chat with ${userName}`);
  };

  const handleShare = (connect) => {
    const shareText = `Check out this connect: ${connect.ride.from} to ${connect.ride.to} on ${connect.ride.date} at ${connect.ride.time}`;
    if (navigator.share) {
      navigator.share({
        title: 'LinkCab Connect',
        text: shareText,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(shareText);
      alert('Connect details copied to clipboard!');
    }
  };

  const handleChat = (connectId) => {
    navigate(`/chat/${connectId}`);
  };

  const getConnectTypeColor = (type) => {
    switch (type) {
      case 'premium': return 'bg-purple-100 text-purple-800';
      case 'standard': return 'bg-blue-100 text-blue-800';
      case 'budget': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getConnectTypeIcon = (type) => {
    switch (type) {
      case 'premium': return '💎';
      case 'standard': return '⭐';
      case 'budget': return '💰';
      default: return '🔗';
    }
  };

  const getPaymentMethods = () => {
    return [
      {
        id: 'wallet',
        name: 'LinkCab Wallet',
        icon: '💳',
        balance: walletBalance,
        description: `Balance: ₹${walletBalance}`
      },
      {
        id: 'card',
        name: 'Credit/Debit Card',
        icon: '💳',
        description: 'Pay with your card'
      },
      {
        id: 'upi',
        name: 'UPI',
        icon: '📱',
        description: 'Pay via UPI'
      }
    ];
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading connects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Premium Connects</h1>
            <p className="text-gray-600">Connect with verified drivers and passengers</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Wallet className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-900">Wallet Balance</span>
              </div>
              <div className="text-lg font-bold text-blue-600">₹{walletBalance}</div>
            </div>
          </div>
        </div>

        {/* Connected Rides Section */}
        {connectedConnects.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Connected Rides</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {connectedConnects.map((connect) => (
                <div key={connect.id} className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <img
                        src={connect.user.avatar}
                        alt={connect.user.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <h3 className="font-semibold text-gray-900">{connect.user.name}</h3>
                        <p className="text-sm text-gray-600">{connect.ride.from} → {connect.ride.to}</p>
                      </div>
                    </div>
                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                      Connected
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      {connect.ride.date} at {connect.ride.time}
                    </div>
                    <button
                      onClick={() => handleChat(connect.id)}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span>Chat</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search connects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="premium">Premium</option>
            <option value="standard">Standard</option>
            <option value="budget">Budget</option>
          </select>
        </div>

        {/* Gender Specificity Toggle */}
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="genderSpecificity"
                checked={genderSpecificity}
                onChange={(e) => setGenderSpecificity(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="genderSpecificity" className="text-sm font-medium text-gray-900">
                Gender Specificity
              </label>
            </div>
            {genderSpecificity && (
              <select
                value={preferredGender}
                onChange={(e) => setPreferredGender(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="any">Any Gender</option>
                <option value="male">Male Only</option>
                <option value="female">Female Only</option>
              </select>
            )}
          </div>
          {genderSpecificity && (
            <p className="text-xs text-gray-600 mt-2">
              Showing connects with {preferredGender === 'any' ? 'any gender' : preferredGender + ' users only'}
            </p>
          )}
        </div>
      </div>

      {/* Connects Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredConnects.map((connect) => (
          <div key={connect.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <img
                    src={connect.user.avatar}
                    alt={connect.user.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold text-gray-900">{connect.user.name}</h3>
                      {connect.user.verified && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">✓ Verified</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-sm text-gray-600">{connect.user.rating}</span>
                      <span className="text-sm text-gray-500">({connect.user.rides} rides)</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleFavorite(connect.id)}
                    className={`p-2 rounded-full transition-colors ${
                      favorites.includes(connect.id)
                        ? 'text-red-500 bg-red-50'
                        : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                    }`}
                  >
                    <Heart className={`w-5 h-5 ${favorites.includes(connect.id) ? 'fill-current' : ''}`} />
                  </button>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getConnectTypeColor(connect.connectType)}`}>
                    {getConnectTypeIcon(connect.connectType)} {connect.connectType.charAt(0).toUpperCase() + connect.connectType.slice(1)}
                  </span>
                </div>
              </div>

              {/* Route */}
              <div className="flex items-center space-x-3 mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-green-500" />
                    <span className="font-medium">{connect.ride.from}</span>
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <Navigation className="w-4 h-4 text-red-500" />
                    <span className="font-medium">{connect.ride.to}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">₹{connect.connectPrice}</div>
                  <div className="text-sm text-gray-500 line-through">₹{connect.originalPrice}</div>
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span>{connect.ride.date}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span>{connect.ride.time}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span>{connect.ride.seats} seat{connect.ride.seats !== 1 ? 's' : ''} available</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Car className="w-4 h-4 text-gray-400" />
                  <span>{connect.ride.vehicle}</span>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="p-6 border-b border-gray-100">
              <p className="text-gray-600 text-sm mb-3">{connect.description}</p>
              <div className="flex flex-wrap gap-2">
                {connect.preferences.map((pref, index) => (
                  <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    {pref}
                  </span>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleConnect(connect)}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Connect for ₹{connect.connectPrice}
                  </button>
                  <button
                    onClick={() => handleCall(connect.user.phone)}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Call"
                  >
                    <Phone className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleMessage(connect.user.name)}
                    className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Message"
                  >
                    <MessageCircle className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleShare(connect)}
                    className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    title="Share"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredConnects.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No connects found</h3>
          <p className="text-gray-600">Try adjusting your search criteria or check back later.</p>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedConnect && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            {paymentSuccess ? (
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment Successful!</h3>
                <p className="text-gray-600 mb-4">You've successfully connected with {selectedConnect.user.name}</p>
                <p className="text-sm text-blue-600">Redirecting to chat...</p>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Complete Connection</h3>
                
                <div className="mb-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <img
                      src={selectedConnect.user.avatar}
                      alt={selectedConnect.user.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium">{selectedConnect.user.name}</p>
                      <p className="text-sm text-gray-600">{selectedConnect.ride.from} → {selectedConnect.ride.to}</p>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Connect Fee:</span>
                      <span className="font-semibold">₹{selectedConnect.connectPrice}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm text-gray-500">
                      <span>Original Price:</span>
                      <span className="line-through">₹{selectedConnect.originalPrice}</span>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                  <div className="space-y-2">
                    {getPaymentMethods().map((method) => (
                      <label key={method.id} className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value={method.id}
                          checked={paymentMethod === method.id}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="text-blue-600"
                        />
                        <span className="text-lg">{method.icon}</span>
                        <div className="flex-1">
                          <span className="text-sm font-medium">{method.name}</span>
                          <p className="text-xs text-gray-500">{method.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={paymentLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePayment}
                    disabled={paymentLoading}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {paymentLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      `Pay ₹${selectedConnect.connectPrice}`
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Connects; 